if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        let bundleFromCart = false;
        try {
          // quick add button adds the variant_id directly
          let pid = formData.get('id');
          // ShopifyAnalytics.meta.product only exists on product pages
          const currentProduct = window.ShopifyAnalytics?.meta?.product;
          const currentProductVariantIds = (currentProduct?.variants ?? []).map(v => v.id.toString());
          // Add tag to the product item
          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            pid = quickAddModal.id.split("-")[1];
          } else if (currentProductVariantIds.includes(pid)) {
            pid = currentProduct.id.toString();
          }

          if (hasUpsellId("cart", pid)) {
            formData.append("properties[_upsellType]", "cart");
          } else if (hasUpsellId("product", pid)) {
            formData.append("properties[_upsellType]", "product");
          } else if (hasUpsellId("rec", pid)) {
            formData.append("properties[_upsellType]", "recommendation");
          }
          // complementary, related or free_shipping; only the cart and product upsells set it
          const upsellIntent = getUpsellIntent(pid);
          if (upsellIntent && (hasUpsellId("cart", pid) || hasUpsellId("product", pid))) {
            formData.append("properties[_upsellIntent]", upsellIntent);
          }
          bundleFromCart = upsellIntent === "bundle" && hasUpsellId("cart", pid);
        }
        catch (e) {
          // Ignore
        }
        config.body = this.withBundleOffer(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }
            if (bundleFromCart) this.markBundleOffer();
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // A ticked buy-together offer (snippets/bundle-offer.liquid) adds its product with the same
      // quantity. Adding two lines takes the items[] format, so the product's own fields move
      // into items[0].
      withBundleOffer(formData) {
        const offerVariantId = formData.get('bundle_offer_variant');
        if (!offerVariantId) return formData;

        const body = new FormData();
        for (const [key, value] of formData) {
          if (key === 'bundle_offer_variant') continue;
          if (key.startsWith('properties[')) {
            body.append(`items[0][properties]${key.slice('properties'.length)}`, value);
          } else if (['id', 'quantity', 'selling_plan'].includes(key)) {
            body.append(`items[0][${key}]`, value);
          } else {
            body.append(key, value);
          }
        }
        body.append('items[1][id]', offerVariantId);
        body.append('items[1][quantity]', formData.get('quantity') || 1);
        body.append('items[1][properties][_upsellType]', 'product');
        body.append('items[1][properties][_upsellIntent]', 'bundle');
        // Shopify Bundles drops line properties when it expands a bundle at checkout, so the offered
        // product's tags never reach the order. The main product isn't a bundle and keeps its own.
        body.append('items[0][properties][_bundleOffer]', 'product');
        return body;
      }

      // The drawer's bundle row adds the offered product to a cart that already has the main product.
      // Its line tags are lost the same way (see withBundleOffer), so the cart carries the mark instead.
      markBundleOffer() {
        fetch(window.routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes: { _bundleOffer: 'cart' } }),
        }).catch((e) => console.error(e));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
